import { useState } from 'react';
import { AppLayout } from '@/components/layout/app-layout';
import { PageHeader } from '@/components/layout/page-header';
import { useQuery } from '@tanstack/react-query';
import { useMandateContext } from '@/contexts/mandate-context';
import { getVatSummary } from '@/services/bookingService';
import { formatSwissCurrency } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Download } from 'lucide-react';

const VatReport = () => {
  const { selectedMandateId } = useMandateContext();
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
  
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter);

  const { data, isLoading } = useQuery({
    queryKey: ['vat-summary', selectedMandateId],
    queryFn: () => selectedMandateId 
      ? getVatSummary(selectedMandateId)
      : null,
    enabled: !!selectedMandateId
  });

  const handleExportCsv = () => {
    if (!data) return;
    
    const csv = [
      'Swiss VAT Report',
      `Quarter: Q${selectedQuarter} ${selectedYear}`,
      '',
      'Category,Amount CHF',
      `Input VAT (deductible),${data.inputVat.toFixed(2)}`,
      `Output VAT (owed),${data.outputVat.toFixed(2)}`,
      `Net VAT payable,${data.netVat.toFixed(2)}`,
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vat-report-Q${selectedQuarter}-${selectedYear}.csv`;
    a.click();
  };

  return (
    <AppLayout>
      <PageHeader 
        title="Swiss VAT Report (MWST)"
        subtitle="Quarterly VAT calculation for ESTV submission"
      />
      <main className="flex-1 p-page-padding lg:p-page-padding-lg">

        {/* Quarter selector */}
        <div className="mt-6 flex items-center gap-4">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 rounded-lg border border-brand-border bg-white"
          >
            {[currentYear, currentYear - 1, currentYear - 2].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          
          <div className="flex gap-2">
            {[1, 2, 3, 4].map(q => (
              <button
                key={q}
                onClick={() => setSelectedQuarter(q)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedQuarter === q
                    ? 'bg-brand-primary text-white'
                    : 'bg-brand-secondary text-brand-primary hover:bg-brand-accent'
                }`}
              >
                Q{q}
              </button>
            ))}
          </div>

          <Button
            onClick={handleExportCsv}
            disabled={!data}
            className="ml-auto"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* VAT Summary Cards */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {isLoading ? (
            <>
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
              <Skeleton className="h-32" />
            </>
          ) : (
            <>
              <div className="bg-white p-6 rounded-lg border border-brand-border">
                <p className="text-sm text-brand-text-secondary mb-2">Input VAT (Deductible)</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatSwissCurrency(data?.inputVat || 0)}
                </p>
                <p className="text-xs text-brand-text-secondary mt-1">
                  Vorsteuer - From expenses
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-brand-border">
                <p className="text-sm text-brand-text-secondary mb-2">Output VAT (Owed)</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatSwissCurrency(data?.outputVat || 0)}
                </p>
                <p className="text-xs text-brand-text-secondary mt-1">
                  Umsatzsteuer - From revenue
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-brand-border">
                <p className="text-sm text-brand-text-secondary mb-2">Net VAT Payable</p>
                <p className={`text-2xl font-bold ${
                  (data?.netVat || 0) >= 0 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {formatSwissCurrency(data?.netVat || 0)}
                </p>
                <p className="text-xs text-brand-text-secondary mt-1">
                  {(data?.netVat || 0) >= 0 ? 'To pay to ESTV' : 'Refund from ESTV'}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Swiss VAT Form Breakdown */}
        <div className="mt-6 bg-white rounded-lg border border-brand-border p-6">
          <h3 className="text-lg font-semibold mb-4">Swiss VAT Form (ESTV) Breakdown</h3>
          
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-brand-secondary">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium">Ziffer (Line)</th>
                  <th className="px-4 py-2 text-left text-sm font-medium">Description</th>
                  <th className="px-4 py-2 text-right text-sm font-medium">Amount CHF</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-brand-border">
                  <td className="px-4 py-2 font-mono">200</td>
                  <td className="px-4 py-2">Gesamterlös (Total revenue incl. VAT)</td>
                  <td className="px-4 py-2 text-right">
                    {formatSwissCurrency((data?.outputVat || 0) / 0.081 + (data?.outputVat || 0))}
                  </td>
                </tr>
                <tr className="border-b border-brand-border">
                  <td className="px-4 py-2 font-mono">302</td>
                  <td className="px-4 py-2">Geschuldete Umsatzsteuer 8.1%</td>
                  <td className="px-4 py-2 text-right font-medium text-orange-600">
                    {formatSwissCurrency(data?.outputVat || 0)}
                  </td>
                </tr>
                <tr className="border-b border-brand-border">
                  <td className="px-4 py-2 font-mono">400</td>
                  <td className="px-4 py-2">Vorsteuern Material, Dienstleistungen</td>
                  <td className="px-4 py-2 text-right font-medium text-green-600">
                    {formatSwissCurrency(data?.inputVat || 0)}
                  </td>
                </tr>
                <tr className="border-t-2 border-brand-border font-bold">
                  <td className="px-4 py-2 font-mono">500</td>
                  <td className="px-4 py-2">Zu bezahlender Betrag</td>
                  <td className="px-4 py-2 text-right text-red-600">
                    {formatSwissCurrency(data?.netVat || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-4 text-sm text-brand-text-secondary">
          <p>📄 {data?.transactions || 0} transactions included in this period</p>
          <p className="mt-1">
            ⚠️ This report is for informational purposes. Please verify with your tax advisor before submission to ESTV.
          </p>
        </div>
      </main>
    </AppLayout>
  );
};

export default VatReport;
